/**
 * AddContextSheet — modal that lets the user attach a typed note to a node.
 *
 * Phase 6.2 port of design/kaiva-canvas/project/components/app.jsx
 * AddContextSheet. Three kinds (`detail` / `exception` / `correction`)
 * with type-aware prompt suggestions. Cmd/Ctrl+Enter saves; Esc cancels.
 *
 * On submit:
 *   - persists the note to the node's `additionalContext` (canvas hook)
 *   - threads a system note into the chat via the existing
 *     `sendNodeContextMessage` flow (handled by the parent's onSubmit).
 *
 * Backend persistence note: the canvas state JSON already round-trips the
 * `additionalContext` array, so kind/text/timestamp survive reload. A
 * dedicated `Context` table is tracked as a backend gap in REDESIGN_DECISIONS.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Check, Pencil, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TYPE_META, normalizeNodeType } from '@/lib/nodeTypes';
import { useFocusTrap } from '@/hooks/useFocusTrap';

export type ContextKind = 'detail' | 'exception' | 'correction';

interface AddContextSheetProps {
  node: {
    id: string;
    label: string;
    type: string;
    description?: string;
  };
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (nodeId: string, kind: ContextKind, text: string) => void | Promise<void>;
}

const KIND_META: Record<ContextKind, { label: string; hint: string; placeholder: string }> = {
  detail: {
    label: 'Detail',
    hint: 'Add nuance Kaiva couldn’t pick up from the conversation.',
    placeholder:
      'e.g. “We also check the customer’s tier before assigning — Tier 1 customers go to Sam directly.”',
  },
  exception: {
    label: 'Exception',
    hint: 'When the usual flow doesn’t apply — edge cases, fallbacks.',
    placeholder:
      'e.g. “If it’s after 6pm, this skips the queue and pages whoever is on call.”',
  },
  correction: {
    label: 'Correction',
    hint: 'Something here is wrong. Tell Kaiva what to fix.',
    placeholder: 'e.g. “It’s actually Linear, not Jira — we migrated last quarter.”',
  },
};

const PROMPT_SUGGESTIONS: Record<string, { kind: ContextKind; label: string }[]> = {
  step: [
    { kind: 'detail', label: 'A detail Kaiva missed' },
    { kind: 'exception', label: 'An exception or edge case' },
    { kind: 'correction', label: 'Something to correct' },
  ],
  actor: [
    { kind: 'detail', label: 'More about this person/team' },
    { kind: 'exception', label: 'When they’re unavailable' },
    { kind: 'correction', label: 'Fix who this really is' },
  ],
  tool: [
    { kind: 'detail', label: 'How this tool is used' },
    { kind: 'exception', label: 'When it breaks or is bypassed' },
    { kind: 'correction', label: 'Wrong tool — fix it' },
  ],
  insight: [
    { kind: 'detail', label: 'Background on this rule' },
    { kind: 'exception', label: 'When it doesn’t apply' },
    { kind: 'correction', label: 'This isn’t quite right' },
  ],
  trigger: [
    { kind: 'detail', label: 'More about this trigger' },
    { kind: 'exception', label: 'When it gets missed' },
    { kind: 'correction', label: 'Wrong trigger — fix it' },
  ],
};

export function AddContextSheet({
  node,
  isSubmitting = false,
  onClose,
  onSubmit,
}: AddContextSheetProps) {
  const [text, setText] = useState('');
  const [kind, setKind] = useState<ContextKind>('detail');
  const [done, setDone] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  // Sheet manages its own initial focus (textarea below); the trap only
  // owns Tab cycling + focus-restore.
  useFocusTrap(sheetRef, { initialFocus: 'none' });

  const typeKey = normalizeNodeType(node.type);
  const meta = typeKey ? TYPE_META[typeKey] : null;
  const typeColor = meta ? `hsl(${meta.color})` : 'hsl(var(--muted-foreground))';
  const typeLowerLabel = meta?.label.toLowerCase() ?? 'item';

  // Auto-focus textarea on open
  useEffect(() => {
    const t = window.setTimeout(() => textareaRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, []);

  // Esc closes (unless we're already in the "saved" success state)
  useEffect(() => {
    if (done) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [done, onClose]);

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed || isSubmitting) return;
    void Promise.resolve(onSubmit(node.id, kind, trimmed)).then(() => {
      setDone(true);
      window.setTimeout(() => onClose(), 1100);
    });
  };

  const choosePrompt = (p: { kind: ContextKind }) => {
    setKind(p.kind);
    window.setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const promptList = PROMPT_SUGGESTIONS[typeKey ?? 'step'] ?? PROMPT_SUGGESTIONS.step;
  const placeholder = KIND_META[kind].placeholder;

  return (
    <>
      {/* Overlay */}
      <motion.div
        className="fixed inset-0 z-[60]"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        style={{
          background: 'hsl(var(--foreground) / 0.18)',
          backdropFilter: 'blur(3px)',
        }}
      />

      {/* Sheet */}
      <motion.div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Add context to ${node.label}`}
        className="fixed left-1/2 top-1/2 z-[61] overflow-auto"
        initial={{ opacity: 0, x: '-50%', y: 'calc(-50% + 14px)', scale: 0.97 }}
        animate={{ opacity: 1, x: '-50%', y: '-50%', scale: 1 }}
        exit={{ opacity: 0, x: '-50%', y: 'calc(-50% + 14px)', scale: 0.97 }}
        transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
        style={{
          width: 480,
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: 'calc(100vh - 80px)',
          padding: '22px 24px 20px',
          background: 'hsl(var(--background))',
          borderRadius: 16,
          boxShadow: 'var(--shadow-3), 0 0 0 1px hsl(var(--border))',
        }}
      >
        {/* Header */}
        <div
          className="mb-3.5 flex items-start justify-between gap-3 pb-3.5"
          style={{ borderBottom: '1px solid hsl(var(--border))' }}
        >
          <div className="min-w-0 flex-1">
            <div
              className="mb-2 inline-flex items-center gap-1.5 kv-eyebrow kv-eyebrow--accent"
              style={{
                letterSpacing: '0.1em',
              }}
            >
              <Pencil className="h-2.5 w-2.5" />
              Add context
            </div>
            <div
              className="flex items-center gap-2.5"
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: 'hsl(var(--foreground))',
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
              }}
            >
              <span
                aria-hidden="true"
                className="inline-block flex-shrink-0"
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 2.5,
                  background: typeColor,
                }}
              />
              <span className="truncate">{node.label}</span>
            </div>
            {node.description && (
              <div
                className="mt-1 ml-[18px] truncate"
                style={{ fontSize: 12.5, color: 'hsl(var(--muted-foreground))' }}
              >
                {node.description}
              </div>
            )}
          </div>
          <CloseBtn onClick={onClose} />
        </div>

        {!done ? (
          <>
            <div
              className="mb-3.5"
              style={{ fontSize: 13, lineHeight: 1.5, color: 'hsl(var(--muted-foreground))' }}
            >
              Tell Kaiva something it missed about this{' '}
              <strong style={{ color: 'hsl(var(--foreground))', fontWeight: 600 }}>
                {typeLowerLabel}
              </strong>
              . It will be woven into the interview and reflected on the canvas.
            </div>

            {/* Kind picker */}
            <div
              className="mb-2 flex gap-1 rounded-[9px]"
              role="tablist"
              style={{ padding: 4, background: 'hsl(var(--muted))' }}
            >
              {(Object.keys(KIND_META) as ContextKind[]).map((k) => {
                const isActive = kind === k;
                return (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setKind(k)}
                    className="flex-1 transition-all duration-150"
                    style={{
                      padding: '7px 10px',
                      borderRadius: 6,
                      border: 'none',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 12.5,
                      fontWeight: 500,
                      cursor: 'pointer',
                      background: isActive ? 'hsl(var(--background))' : 'transparent',
                      color: isActive ? 'hsl(var(--sienna-deep))' : 'hsl(var(--muted-foreground))',
                      boxShadow: isActive ? 'var(--shadow-1)' : undefined,
                    }}
                  >
                    {KIND_META[k].label}
                  </button>
                );
              })}
            </div>
            <div
              className="mb-3 italic"
              style={{ fontSize: 11.5, color: 'hsl(var(--muted-foreground))', padding: '0 2px' }}
            >
              {KIND_META[kind].hint}
            </div>

            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={placeholder}
              rows={4}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              className="ctx-textarea-input w-full resize-vertical outline-none"
              style={{
                padding: '12px 14px',
                border: '1px solid hsl(var(--border))',
                borderRadius: 10,
                fontFamily: 'var(--font-sans)',
                fontSize: 13.5,
                lineHeight: 1.55,
                color: 'hsl(var(--foreground))',
                background: 'hsl(var(--background))',
                minHeight: 92,
                boxSizing: 'border-box',
                transition: 'border-color 150ms ease, box-shadow 150ms ease',
              }}
            />

            {/* Prompt suggestions */}
            <div
              className="mt-3.5 mb-2 kv-eyebrow"
              style={{
                fontSize: 10,
                letterSpacing: '0.1em',
                color: 'hsl(var(--muted-foreground))',
              }}
            >
              Or start from…
            </div>
            <div className="flex flex-wrap gap-1.5">
              {promptList.map((p, i) => {
                const isActive = kind === p.kind;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => choosePrompt(p)}
                    className="transition-all duration-150"
                    style={{
                      padding: '6px 11px',
                      background: isActive
                        ? 'hsl(var(--sienna-soft))'
                        : 'hsl(var(--background))',
                      border: `1px solid ${
                        isActive ? 'hsl(var(--sienna) / 0.4)' : 'hsl(var(--border))'
                      }`,
                      borderRadius: 999,
                      fontFamily: 'var(--font-sans)',
                      fontSize: 12,
                      color: isActive
                        ? 'hsl(var(--sienna-deep))'
                        : 'hsl(var(--muted-foreground))',
                      cursor: 'pointer',
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div
              className="mt-4 flex items-center justify-between gap-3 pt-3.5"
              style={{ borderTop: '1px solid hsl(var(--border))' }}
            >
              <Shortcut>
                <Kbd>⌘</Kbd>
                <Kbd>⏎</Kbd>
                <span style={{ marginLeft: 2 }}>to save</span>
              </Shortcut>
              <div className="flex gap-2">
                <SecondaryBtn onClick={onClose}>Cancel</SecondaryBtn>
                <PrimaryBtn
                  onClick={handleSubmit}
                  disabled={!text.trim() || isSubmitting}
                >
                  {isSubmitting ? (
                    'Saving…'
                  ) : (
                    <>
                      <Check className="h-2.5 w-2.5" />
                      Save context
                    </>
                  )}
                </PrimaryBtn>
              </div>
            </div>
          </>
        ) : (
          <div className="px-2 py-7 text-center" style={{ paddingBottom: 12 }}>
            <div
              className="mx-auto mb-2.5 inline-grid place-items-center rounded-full"
              style={{
                width: 44,
                height: 44,
                background: 'hsl(var(--teal))',
                color: 'hsl(var(--slate-navy))',
              }}
            >
              <Check className="h-4 w-4" />
            </div>
            <div
              style={{
                fontSize: 15.5,
                fontWeight: 600,
                color: 'hsl(var(--foreground))',
                letterSpacing: '-0.01em',
              }}
            >
              Context saved
            </div>
            <div
              className="mt-1"
              style={{ fontSize: 12.5, color: 'hsl(var(--muted-foreground))' }}
            >
              Kaiva will fold this into the next round of the interview.
            </div>
          </div>
        )}
      </motion.div>
    </>
  );
}

function CloseBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      className="inline-grid h-[30px] w-[30px] place-items-center rounded-lg transition-colors"
      style={{
        background: 'hsl(var(--card))',
        border: '1px solid hsl(var(--border))',
        color: 'hsl(var(--muted-foreground))',
      }}
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );
}

function Shortcut({ children }: { children: ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-1"
      style={{ fontSize: 11, color: 'hsl(var(--muted-foreground))' }}
    >
      {children}
    </span>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd
      style={{
        fontSize: 10,
        padding: '1px 5px',
        background: 'hsl(var(--muted))',
        border: '1px solid hsl(var(--border))',
        borderRadius: 3,
        color: 'hsl(var(--foreground))',
        lineHeight: 1.4,
      }}
    >
      {children}
    </kbd>
  );
}

function PrimaryBtn({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-1.5 transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
      style={{
        height: 30,
        padding: '0 12px',
        borderRadius: 8,
        background: 'hsl(var(--sienna))',
        color: 'hsl(var(--cream))',
        border: '1px solid hsl(var(--sienna))',
        fontSize: 12.5,
        fontWeight: 500,
      }}
    >
      {children}
    </button>
  );
}

function SecondaryBtn({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="transition-colors hover:[background:hsl(var(--muted))]"
      style={{
        height: 30,
        padding: '0 12px',
        borderRadius: 8,
        background: 'hsl(var(--card))',
        color: 'hsl(var(--foreground))',
        border: '1px solid hsl(var(--border))',
        fontSize: 12.5,
        fontWeight: 500,
      }}
    >
      {children}
    </button>
  );
}
